import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Trophy, Loader2 } from "lucide-react";

const HERO =
  "https://images.unsplash.com/photo-1696542095330-ecda668b21d5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwzfHxuZmwlMjBmb290YmFsbCUyMHN0YWRpdW0lMjBuaWdodHxlbnwwfHx8fDE3ODY0NzY5NDd8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(username, password);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#0A0A0A] text-white">
      {/* Left: stadium */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img src={HERO} alt="stadium" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute bottom-0 left-0 p-12 z-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-[#E31837] flex items-center justify-center">
              <Trophy className="w-6 h-6" />
            </div>
            <span className="font-['Bebas_Neue'] text-4xl tracking-wide">PICK'EM POOL</span>
          </div>
          <h1 className="font-['Bebas_Neue'] text-6xl leading-none tracking-tight max-w-md">
            2026 SEASON.<br />ONE CHAMPION.
          </h1>
          <p className="text-white/60 mt-4 max-w-sm">
            Make your picks every week. Beat the spread of your friends. Climb the standings.
          </p>
        </div>
      </div>

      {/* Right: form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-8 h-8 bg-[#E31837] flex items-center justify-center">
              <Trophy className="w-5 h-5" />
            </div>
            <span className="font-['Bebas_Neue'] text-2xl tracking-wide">PICK'EM POOL</span>
          </div>

          <h2 className="font-['Bebas_Neue'] text-4xl tracking-tight mb-1">WELCOME BACK</h2>
          <p className="text-white/50 text-sm mb-6">Log in with your username.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-white/50">Username</label>
              <input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="mt-1 w-full bg-[#141414] border border-white/10 px-4 py-3 rounded-sm focus:ring-2 focus:ring-[#E31837] focus:outline-none focus:border-transparent transition-colors duration-200"
                placeholder="e.g. gridiron_greg"
                data-testid="username-input"
                autoComplete="username"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-white/50">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full bg-[#141414] border border-white/10 px-4 py-3 rounded-sm focus:ring-2 focus:ring-[#E31837] focus:outline-none focus:border-transparent transition-colors duration-200"
                placeholder="••••••••"
                data-testid="password-input"
                autoComplete="current-password"
              />
            </div>

            {error && (
              <div className="text-sm text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/30 px-3 py-2" data-testid="auth-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#E31837] hover:bg-[#C8102E] py-3 rounded-sm uppercase tracking-[0.2em] text-sm font-semibold transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="submit-auth-button"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Sign In
            </button>
          </form>

          <p className="text-white/30 text-xs mt-8 leading-relaxed">
            New to the pool? Ask the commissioner for your invite link to create an account. Commissioners sign in here.
          </p>
        </div>
      </div>
    </div>
  );
}
